import { Avatar, Tooltip } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { useStagePresence } from '../../hooks/useStagePresence';
import { useUserPhoto } from '../../hooks/useUserPhoto';
import type { StagePresenceUser } from '../../types/websocket.types';

interface Props {
  stageKey: string;
}

/**
 * Детерминированный цвет аватара на основе userId
 */
function getAvatarColor(userId: string): string {
  const colors = [
    '#1677ff', '#52c41a', '#faad14', '#f5222d',
    '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16',
  ];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

/**
 * Инициалы из имени пользователя
 */
function getInitials(userName: string): string {
  return userName
    .split(' ')
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Аватар одного presence-пользователя с загрузкой фото из MS Graph
 */
function PresenceAvatar({ user, size = 22 }: { user: StagePresenceUser; size?: number }) {
  const { data: photoUrl } = useUserPhoto(user.userId);

  return (
    <Avatar
      src={photoUrl ?? undefined}
      size={size}
      style={{
        backgroundColor: photoUrl ? undefined : getAvatarColor(user.userId),
        fontSize: size <= 18 ? 9 : 10,
        border: '1.5px solid rgba(255, 255, 255, 0.2)',
        flexShrink: 0,
      }}
    >
      {!photoUrl && getInitials(user.userName)}
    </Avatar>
  );
}

/**
 * Показывает кто сейчас работает на этом этапе.
 * Данные приходят через WebSocket (presence:stageKey канал) — без HTTP-запросов.
 * Фото загружаются из MS Graph по userId (кэшируются 30 минут).
 * Размещается в AppHeader рядом с иконками.
 */
export function StagePresence({ stageKey }: Props) {
  const { users } = useStagePresence(stageKey);

  if (users.length === 0) return null;

  return (
    <Tooltip
      title={
        <div style={{ padding: '4px 0' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
            <TeamOutlined />
            <span>Сейчас на этапе</span>
          </div>
          {users.map((user) => (
            <div key={user.userId} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <PresenceAvatar user={user} size={18} />
              <span style={{ fontSize: 12 }}>{user.userName}</span>
            </div>
          ))}
        </div>
      }
      placement="bottomRight"
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '3px 8px',
          borderRadius: 999,
          background: 'rgba(255, 255, 255, 0.06)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          cursor: 'default',
          transition: 'background 150ms ease',
        }}
        aria-label={`${users.length} на этапе`}
      >
        <Avatar.Group
          max={{
            count: 3,
            style: {
              backgroundColor: 'rgba(255, 255, 255, 0.12)',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: 10,
              border: '1.5px solid rgba(255, 255, 255, 0.2)',
            },
          }}
          size={22}
        >
          {users.map((user) => (
            <PresenceAvatar key={user.userId} user={user} size={22} />
          ))}
        </Avatar.Group>
      </div>
    </Tooltip>
  );
}
